import { apiGet, apiPut } from './index'
import {
  normalizePrepDashboardSettings,
  type PrepDashboardSettings,
  type PrepDateBasis,
  type RawPrepDashboardSettings,
} from './prep'

export type PrepLayout = 'columns' | 'single' | 'dashboard'

export type PrepAgeMaxDays = 0 | 1 | 2 | 3 | 7
export type { PrepDateBasis } from './prep'

export type LiveAdvanceMode = 'auto' | 'hybrid' | 'manual'
export type ShowClockLiveTheme = 'inherit' | 'light' | 'dark'

interface RawUserSettingsResponse {
  user_id: number
  chat_list_preview_lines: number
  prep?: {
    drawer_font_scale?: number
    drawerFontScale?: number
    layout?: string
    single_layout_split?: number
    singleLayoutSplit?: number
    headline_lines?: number
    headlineLines?: number
    age_max_days?: number
    ageMaxDays?: number
    date_basis?: unknown
    dateBasis?: unknown
    search_include_dismissed?: unknown
    searchIncludeDismissed?: unknown
    list_show_images?: unknown
    listShowImages?: unknown
    list_show_preview_text?: unknown
    listShowPreviewText?: unknown
  }
  prepHeadlineLines?: number
  clock_fuzzy?: boolean
  prep_dashboard?: RawPrepDashboardSettings
  prepDashboard?: RawPrepDashboardSettings
  prep_age_max_days?: number
  prepAgeMaxDays?: number
  prep_date_basis?: unknown
  prepDateBasis?: unknown
  prep_search_include_dismissed?: unknown
  prepSearchIncludeDismissed?: unknown
  prep_list_show_images?: unknown
  prepListShowImages?: unknown
  prep_list_show_preview_text?: unknown
  prepListShowPreviewText?: unknown
  live_advance_mode?: unknown
  liveAdvanceMode?: unknown
  live_auto_mode?: unknown
  liveAutoMode?: unknown
  show_clock?: {
    live_prep_tease?: unknown
    livePrepTease?: unknown
    live_header_visible?: unknown
    liveHeaderVisible?: unknown
    live_header_fill?: unknown
    liveHeaderFill?: unknown
    live_theme?: unknown
    liveTheme?: unknown
  }
  showClock?: {
    live_prep_tease?: unknown
    livePrepTease?: unknown
    live_header_visible?: unknown
    liveHeaderVisible?: unknown
    live_header_fill?: unknown
    liveHeaderFill?: unknown
    live_theme?: unknown
    liveTheme?: unknown
  }
}

export interface UserSettingsResponse {
  user_id: number
  chat_list_preview_lines: number
  prep?: {
    drawerFontScale?: number
    layout?: PrepLayout
    singleLayoutSplit?: number
    headlineLines: number
    ageMaxDays?: PrepAgeMaxDays
    dateBasis?: PrepDateBasis
    searchIncludeDismissed?: boolean
    listShowImages?: boolean
    listShowPreviewText?: boolean
  }
  clock_fuzzy: boolean
  liveAdvanceMode: LiveAdvanceMode
  showClock?: {
    livePrepTease?: boolean
    liveHeaderVisible?: boolean
    liveHeaderFill?: boolean
    liveTheme?: ShowClockLiveTheme
  }
  /**
   * Backwards compatibility for older clients; new code should use `liveAdvanceMode`.
   */
  liveAutoMode?: boolean
  prepDashboard?: PrepDashboardSettings
}

export interface UpdateUserSettingsPayload {
  chat_list_preview_lines?: number
  prep?: {
    drawerFontScale?: number
    layout?: PrepLayout
    singleLayoutSplit?: number
    headlineLines?: number
    ageMaxDays?: PrepAgeMaxDays
    dateBasis?: PrepDateBasis
    searchIncludeDismissed?: boolean
    listShowImages?: boolean
    listShowPreviewText?: boolean
  }
  clock_fuzzy?: boolean
  liveAdvanceMode?: LiveAdvanceMode
  liveAutoMode?: boolean
  prepDashboard?: Partial<PrepDashboardSettings>
  showClock?: {
    livePrepTease?: boolean
    liveHeaderVisible?: boolean
    liveHeaderFill?: boolean
    liveTheme?: ShowClockLiveTheme
  }
}

const normalizeBooleanFlag = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (normalized === '') {
      return fallback
    }
    if (['1', 'true', 'yes', 'on'].includes(normalized)) {
      return true
    }
    if (['0', 'false', 'no', 'off'].includes(normalized)) {
      return false
    }
    return fallback
  }
  if (typeof value === 'number') {
    return value !== 0
  }
  if (value === null || value === undefined) {
    return fallback
  }
  return fallback
}

const normalizeHeadlineLines = (value: unknown): number => {
  const fallback = 2

  const toNumber = () => {
    if (typeof value === 'number') {
      return value
    }
    if (typeof value === 'string') {
      const parsed = Number.parseInt(value, 10)
      return Number.isFinite(parsed) ? parsed : Number.NaN
    }
    if (typeof value === 'boolean') {
      return value ? 1 : 0
    }
    if (value instanceof Number) {
      return value.valueOf()
    }
    return Number(value)
  }

  const numeric = toNumber()
  if (!Number.isFinite(numeric)) {
    return fallback
  }

  const rounded = Math.round(numeric)
  const clamped = Math.min(3, Math.max(1, rounded))
  return clamped
}

const normalizeUserSettingsResponse = (raw: RawUserSettingsResponse): UserSettingsResponse => {
  const drawerFontScale = raw.prep?.drawerFontScale ?? raw.prep?.drawer_font_scale
  const rawLayout = raw.prep?.layout
  const layout: PrepLayout | undefined =
    rawLayout === 'single' || rawLayout === 'columns' || rawLayout === 'dashboard' ? rawLayout : undefined
  const rawSplit = raw.prep?.singleLayoutSplit ?? raw.prep?.single_layout_split
  const singleLayoutSplit =
    typeof rawSplit === 'number' && Number.isFinite(rawSplit)
      ? rawSplit
      : typeof rawSplit === 'string'
        ? Number.parseFloat(rawSplit)
        : undefined
  const rawHeadlineLines = raw.prep?.headlineLines ?? raw.prep?.headline_lines ?? raw.prepHeadlineLines
  const headlineLines = normalizeHeadlineLines(rawHeadlineLines)
  const resolveAgeMaxDays = (value: unknown): PrepAgeMaxDays | undefined => {
    const allowed: PrepAgeMaxDays[] = [0, 1, 2, 3, 7]
    const numeric = typeof value === 'string' ? Number.parseInt(value, 10) : Number(value)
    if (!Number.isFinite(numeric)) {
      return undefined
    }
    const candidate = allowed.find((option) => option === numeric)
    return candidate ?? undefined
  }
  const normalizeSearchIncludeDismissed = (value: unknown): boolean => {
    if (typeof value === 'boolean') {
      return value
    }
    if (typeof value === 'number') {
      return value !== 0
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      if (normalized === '') {
        return false
      }
      if (['1', 'true', 'yes', 'on'].includes(normalized)) {
        return true
      }
      if (['0', 'false', 'no', 'off'].includes(normalized)) {
        return false
      }
    }
    return Boolean(value)
  }
  const normalizeListShowImages = (value: unknown): boolean => {
    if (typeof value === 'boolean') {
      return value
    }
    if (typeof value === 'number') {
      return value !== 0
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      if (normalized === '') {
        return false
      }
      if (['1', 'true', 'yes', 'on'].includes(normalized)) {
        return true
      }
      if (['0', 'false', 'no', 'off'].includes(normalized)) {
        return false
      }
    }
    return Boolean(value)
  }
  const normalizeListShowPreviewText = (value: unknown): boolean => {
    if (typeof value === 'boolean') {
      return value
    }
    if (typeof value === 'number') {
      return value !== 0
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      if (normalized === '') {
        return true
      }
      if (['1', 'true', 'yes', 'on'].includes(normalized)) {
        return true
      }
      if (['0', 'false', 'no', 'off'].includes(normalized)) {
        return false
      }
    }
    return Boolean(value)
  }
  const rawAgeFromPrep =
    raw.prep?.ageMaxDays ?? raw.prep?.age_max_days ?? raw.prepAgeMaxDays ?? raw.prep_age_max_days
  const ageMaxDays = resolveAgeMaxDays(rawAgeFromPrep)
  const rawDateBasis =
    raw.prep?.dateBasis ?? raw.prep?.date_basis ?? raw.prepDateBasis ?? raw.prep_date_basis
  const dateBasis: PrepDateBasis | undefined =
    rawDateBasis === 'created_at' || rawDateBasis === 'published_at' ? rawDateBasis : undefined
  const searchIncludeDismissedRaw =
    raw.prep?.searchIncludeDismissed ??
    raw.prep?.search_include_dismissed ??
    raw.prepSearchIncludeDismissed ??
    raw.prep_search_include_dismissed
  const searchIncludeDismissed =
    searchIncludeDismissedRaw !== undefined
      ? normalizeSearchIncludeDismissed(searchIncludeDismissedRaw)
      : undefined
  const listShowImagesRaw =
    raw.prep?.listShowImages ??
    raw.prep?.list_show_images ??
    raw.prepListShowImages ??
    raw.prep_list_show_images
  const listShowImages =
    listShowImagesRaw !== undefined ? normalizeListShowImages(listShowImagesRaw) : undefined
  const listShowPreviewTextRaw =
    raw.prep?.listShowPreviewText ??
    raw.prep?.list_show_preview_text ??
    raw.prepListShowPreviewText ??
    raw.prep_list_show_preview_text
  const listShowPreviewText =
    listShowPreviewTextRaw !== undefined
      ? normalizeListShowPreviewText(listShowPreviewTextRaw)
      : undefined

  const prep =
    drawerFontScale !== undefined ||
    layout !== undefined ||
    singleLayoutSplit !== undefined ||
    raw.prep ||
    ageMaxDays !== undefined ||
    dateBasis !== undefined ||
    searchIncludeDismissedRaw !== undefined ||
    listShowImagesRaw !== undefined ||
    listShowPreviewTextRaw !== undefined
      ? {
          ...(drawerFontScale !== undefined ? { drawerFontScale } : {}),
          ...(layout !== undefined ? { layout } : {}),
          ...(singleLayoutSplit !== undefined && Number.isFinite(singleLayoutSplit)
            ? { singleLayoutSplit }
            : {}),
          headlineLines,
          ...(ageMaxDays !== undefined ? { ageMaxDays } : {}),
          ...(dateBasis !== undefined ? { dateBasis } : {}),
          ...(searchIncludeDismissed !== undefined
            ? { searchIncludeDismissed }
            : {}),
          ...(listShowImages !== undefined ? { listShowImages } : {}),
          ...(listShowPreviewText !== undefined ? { listShowPreviewText } : {}),
        }
      : {
          headlineLines,
        }

  const prepDashboardRaw = raw.prepDashboard ?? raw.prep_dashboard
  const prepDashboard = prepDashboardRaw ? normalizePrepDashboardSettings(prepDashboardRaw) : undefined

  const rawAdvanceMode = raw.liveAdvanceMode ?? raw.live_advance_mode
  const liveAdvanceMode: LiveAdvanceMode = (() => {
    if (typeof rawAdvanceMode === 'string') {
      const normalized = rawAdvanceMode.trim().toLowerCase()
      if (normalized === 'auto' || normalized === 'hybrid' || normalized === 'manual') {
        return normalized
      }
    }

    const liveAutoModeRaw = raw.liveAutoMode ?? raw.live_auto_mode
    if (liveAutoModeRaw !== undefined) {
      return normalizeBooleanFlag(liveAutoModeRaw, false) ? 'auto' : 'manual'
    }

    return 'hybrid'
  })()

  const liveAutoMode = liveAdvanceMode === 'auto'
  const rawShowClock = raw.showClock ?? raw.show_clock
  const normalizeShowClockTheme = (value: unknown): ShowClockLiveTheme | undefined => {
    if (typeof value !== 'string') {
      return undefined
    }
    const normalized = value.trim().toLowerCase()
    if (normalized === 'light' || normalized === 'dark' || normalized === 'inherit') {
      return normalized
    }
    return undefined
  }
  const showClock =
    rawShowClock && typeof rawShowClock === 'object'
      ? {
          livePrepTease:
            'livePrepTease' in rawShowClock
              ? normalizeBooleanFlag((rawShowClock as { livePrepTease?: unknown }).livePrepTease, false)
              : 'live_prep_tease' in rawShowClock
                ? normalizeBooleanFlag((rawShowClock as { live_prep_tease?: unknown }).live_prep_tease, false)
                : undefined,
          liveHeaderVisible:
            'liveHeaderVisible' in rawShowClock
              ? normalizeBooleanFlag((rawShowClock as { liveHeaderVisible?: unknown }).liveHeaderVisible, true)
              : 'live_header_visible' in rawShowClock
                ? normalizeBooleanFlag((rawShowClock as { live_header_visible?: unknown }).live_header_visible, true)
                : undefined,
          liveHeaderFill:
            'liveHeaderFill' in rawShowClock
              ? normalizeBooleanFlag((rawShowClock as { liveHeaderFill?: unknown }).liveHeaderFill, true)
              : 'live_header_fill' in rawShowClock
                ? normalizeBooleanFlag((rawShowClock as { live_header_fill?: unknown }).live_header_fill, true)
                : undefined,
          liveTheme:
            'liveTheme' in rawShowClock
              ? normalizeShowClockTheme((rawShowClock as { liveTheme?: unknown }).liveTheme)
              : 'live_theme' in rawShowClock
                ? normalizeShowClockTheme((rawShowClock as { live_theme?: unknown }).live_theme)
                : undefined,
        }
      : undefined

  return {
    user_id: raw.user_id,
    chat_list_preview_lines: raw.chat_list_preview_lines,
    prep,
    clock_fuzzy: raw.clock_fuzzy ?? false,
    liveAdvanceMode,
    liveAutoMode,
    ...(showClock ? { showClock } : {}),
    ...(prepDashboard ? { prepDashboard } : {}),
  }
}

export const getUserSettings = async (): Promise<UserSettingsResponse> => {
  const response = await apiGet<RawUserSettingsResponse>('user/settings')
  return normalizeUserSettingsResponse(response)
}

export const updateUserSettings = async (
  payload: UpdateUserSettingsPayload,
): Promise<UserSettingsResponse> => {
  const body: Record<string, unknown> = {}

  if (payload.chat_list_preview_lines !== undefined) {
    body.chat_list_preview_lines = payload.chat_list_preview_lines
  }

  if (payload.prep) {
    const prepPayload: Record<string, unknown> = {}

    if (payload.prep.drawerFontScale !== undefined) {
      prepPayload.drawerFontScale = payload.prep.drawerFontScale
    }

    if (payload.prep.layout !== undefined) {
      prepPayload.layout = payload.prep.layout
    }

    if (payload.prep.singleLayoutSplit !== undefined) {
      prepPayload.singleLayoutSplit = payload.prep.singleLayoutSplit
    }

    if (payload.prep.headlineLines !== undefined) {
      prepPayload.headlineLines = payload.prep.headlineLines
    }

    if (payload.prep.ageMaxDays !== undefined) {
      prepPayload.ageMaxDays = payload.prep.ageMaxDays
    }

    if (payload.prep.dateBasis !== undefined) {
      prepPayload.date_basis = payload.prep.dateBasis
    }

    if (payload.prep.searchIncludeDismissed !== undefined) {
      prepPayload.searchIncludeDismissed = payload.prep.searchIncludeDismissed
    }

    if (payload.prep.listShowImages !== undefined) {
      prepPayload.listShowImages = payload.prep.listShowImages
    }

    if (payload.prep.listShowPreviewText !== undefined) {
      prepPayload.listShowPreviewText = payload.prep.listShowPreviewText
    }

    if (Object.keys(prepPayload).length > 0) {
      body.prep = prepPayload
    }
  }

  if (payload.clock_fuzzy !== undefined) {
    body.clock_fuzzy = payload.clock_fuzzy
  }

  if (payload.liveAdvanceMode !== undefined) {
    body.live_advance_mode = payload.liveAdvanceMode
  } else if (payload.liveAutoMode !== undefined) {
    body.live_auto_mode = payload.liveAutoMode
  }

  if (payload.prepDashboard) {
    body.prep_dashboard = payload.prepDashboard
  }

  if (payload.showClock) {
    const showClockPayload: Record<string, unknown> = {}
    if (payload.showClock.livePrepTease !== undefined) {
      showClockPayload.live_prep_tease = payload.showClock.livePrepTease
    }
    if (payload.showClock.liveHeaderVisible !== undefined) {
      showClockPayload.live_header_visible = payload.showClock.liveHeaderVisible
    }
    if (payload.showClock.liveHeaderFill !== undefined) {
      showClockPayload.live_header_fill = payload.showClock.liveHeaderFill
    }
    if (payload.showClock.liveTheme !== undefined) {
      showClockPayload.live_theme = payload.showClock.liveTheme
    }
    if (Object.keys(showClockPayload).length > 0) {
      body.show_clock = showClockPayload
    }
  }

  const response = await apiPut<RawUserSettingsResponse>('user/settings', body)
  return normalizeUserSettingsResponse(response)
}
